// @ts-check
// Construtor do público (contrato 09 de público, seção 9): de onde partir (todos os leads ou uma view da escola ao
// vivo), atalhos de visita, matrícula e perdidos, condições E/OU com o mesmo editor do filtro avançado das listas, quem
// recebe e a contagem ao vivo. Usado pelas campanhas de e-mail e de WhatsApp e pela entrada, condição e saída dos fluxos
// (onda 2). O pai recebe cada mudança por `onChange` e grava quando quiser.
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { previewAudience } from '../../data/audiences.js';
import { fieldsById, objectsByNameSingular, viewsState } from '../../data/metadata-store.js';
import { fieldCatalogForAudience } from '../../lib/audience-field-catalog.js';
import { describeAudienceRule } from '../../lib/audience-summary.js';
import { getDefaultOperand } from '../../lib/view-filters.js';
import { Icon } from '../../ui/icon.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { FilterTreeEditor } from '../views/filter-tree-editor.js';
import { AudienceCardsDialog } from './audience-cards-dialog.js';
import { AudienceCountPanel } from './audience-count-panel.js';
import { createAudienceStore } from './audience-store.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { AUDIENCE_LIMITS } from '../../../../shared/audiences/audience-definition.js';

/**
 * @typedef {import('../../../../shared/audiences/audience-definition.js').AudienceDefinition} AudienceDefinition
 * @typedef {import('./audience-count-panel.js').PreviewState} PreviewState
 */

// a prévia espera a última digitação; só um pedido vale por construtor (contador)
export const AUDIENCE_PREVIEW_DEBOUNCE_MILLISECONDS = 600;
const ENROLLMENT_OBJECT = 'enrollmentOpportunity';
// um fluxo mostra até três construtores na mesma tela (entrada, condição e saída): ids próprios por instância
let builderSequence = 0;

/**
 * @param {{ value: AudienceDefinition | null | undefined, onChange?: (definition: AudienceDefinition) => void,
 *   usage?: 'AUDIENCE' | 'CONDITION', channels?: Array<'EMAIL' | 'WHATSAPP'>, purpose?: 'MARKETING' | 'SERVICE',
 *   title?: string, readOnly?: boolean, showRecipients?: boolean, onProblemsChange?: (problems: Array<{ filterId: string | null, code: string }>) => void }} props
 */
export function AudienceBuilder({ value, onChange, usage = 'AUDIENCE', channels = [], purpose = 'MARKETING', title, readOnly = false, showRecipients = true, onProblemsChange }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const store = useMemo(() => createAudienceStore(value, { usage, onChange: (definition) => onChangeRef.current?.(definition) }), []);
  const [previewState, setPreviewState] = useState(/** @type {PreviewState} */ ({ status: 'idle', preview: null, error: null }));
  const [isCardsOpen, setIsCardsOpen] = useState(false);
  const [isMobileCountOpen, setIsMobileCountOpen] = useState(false);
  const [copyProblem, setCopyProblem] = useState(/** @type {string | null} */ (null));
  const requestCounter = useRef(0);
  const idPrefix = useMemo(() => `audience-builder-${(builderSequence += 1)}`, []);
  const objectMetadata = objectsByNameSingular.value.get(ENROLLMENT_OBJECT);
  const getField = (/** @type {string} */ id) => fieldsById.value.get(id);
  const fieldCatalog = objectMetadata ? fieldCatalogForAudience(objectMetadata, { objectsByNameSingular: objectsByNameSingular.value, getField }) : null;
  const stageField = objectMetadata?.fields.find((field) => field.name === 'stage');
  const cardViews = objectMetadata ? viewsState.value.filter((view) => view.objectMetadataId === objectMetadata.id) : [];
  const sourceView = store.viewId.value ? cardViews.find((view) => view.id === store.viewId.value) : undefined;
  const revision = store.revision.value;
  const channelKey = channels.join(',');

  const runPreview = async () => {
    requestCounter.current += 1;
    const requestNumber = requestCounter.current;
    setPreviewState((current) => ({ status: current.preview ? 'updating' : 'loading', preview: current.preview, error: null }));
    try {
      const preview = await previewAudience({ definition: store.toDefinition(), usage, channels: showRecipients ? channels : [], purpose, sampleSize: 20 });
      if (requestNumber !== requestCounter.current) return;
      setPreviewState({ status: 'ready', preview, error: null });
      onProblemsChange?.(preview.problems);
    } catch (error) {
      if (requestNumber !== requestCounter.current) return;
      const statusCode = /** @type {any} */ (error)?.statusCode;
      setPreviewState((current) => ({ status: statusCode === 403 ? 'forbidden' : 'error', preview: statusCode === 403 ? null : current.preview, error: String(error) }));
    }
  };

  useEffect(() => {
    // somente leitura (campanha enviada, fluxo sem WORKFLOWS): sem prévia automática, só pelo botão
    if (readOnly) return undefined;
    const timer = setTimeout(runPreview, revision === 0 ? 0 : AUDIENCE_PREVIEW_DEBOUNCE_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [revision, channelKey, purpose, usage, showRecipients]);

  if (!objectMetadata || !fieldCatalog) return html`<p class="audience-builder-empty">${t('audience.count.noPermission')}</p>`;

  /** @type {Map<string, string>} */
  const problemsByFilterId = new Map();
  for (const problem of previewState.preview?.problems ?? []) if (problem.filterId) problemsByFilterId.set(problem.filterId, problem.code);
  const root = store.filterGroups.value.find((group) => !group.parentViewFilterGroupId);
  const firstField = fieldCatalog.fields[0];
  const visitOptions = ['ANY', 'VISITED', 'NOT_VISITED', 'UPCOMING', 'NO_SHOW'].map((option) => ({
    value: option, label: t(`audience.presets.visit.${option}`), hint: option === 'VISITED' || option === 'NOT_VISITED' ? t(`audience.presets.visit.hints.${option}`) : undefined,
  }));
  const enrollmentOptions = ['ANY', 'ENROLLED', 'NOT_ENROLLED'].map((option) => ({ value: option, label: t(`audience.presets.enrollment.${option}`) }));
  const lostOptions = ['EXCLUDE', 'INCLUDE', 'ONLY'].map((option) => ({ value: option, label: t(`audience.presets.lost.${option}`) }));

  return html`<div class="audience-builder" data-usage=${usage} data-read-only=${readOnly ? 'true' : undefined}>
    <div class="audience-builder-main">
      ${title ? html`<h3 class="audience-builder-title">${title}</h3>` : null}

      <section class="audience-builder-section" aria-labelledby=${`${idPrefix}-source`}>
        <h4 id=${`${idPrefix}-source`} class="audience-builder-section-title">${t('audience.source.label')}</h4>
        <${SegmentedControl}
          label=${t('audience.source.label')}
          value=${store.viewId.value ? 'VIEW' : 'ALL'}
          disabled=${readOnly}
          options=${[{ value: 'ALL', label: t('audience.source.allLeads') }, { value: 'VIEW', label: t('audience.source.view') }]}
          onChange=${(choice) => {
            setCopyProblem(null);
            if (choice === 'ALL') store.setViewId(null);
            else {
              const firstWorkspaceView = cardViews.find((view) => view.visibility !== 'UNLISTED');
              if (firstWorkspaceView) store.setViewId(firstWorkspaceView.id);
            }
          }}
        />
        ${store.viewId.value
          ? html`<div class="audience-source-view">
              <select class="text-input audience-source-select" aria-label=${t('audience.source.view')} value=${store.viewId.value} disabled=${readOnly}
                onChange=${(event) => store.setViewId(event.currentTarget.value)}>
                ${!sourceView ? html`<option value=${store.viewId.value}>${t('audience.source.viewMissing')}</option>` : null}
                ${cardViews.map((view) => html`<option key=${view.id} value=${view.id} disabled=${view.visibility === 'UNLISTED'}>
                  ${translateMetadataLabel(view.name)}${view.visibility === 'UNLISTED' ? ` (${t('audience.source.viewPersonalDisabled')})` : ''}
                </option>`)}
              </select>
              <span class="audience-live-chip"><${Icon} name="IconRefresh" size=${12} />${t('audience.source.viewLive')}</span>
              ${sourceView
                ? html`<div class="audience-view-chips">
                    ${sourceView.viewFilters.map((filter) => {
                      const phrase = describeAudienceRule(/** @type {any} */ (filter), getField);
                      return phrase ? html`<span class="audience-view-chip" key=${filter.id}>${phrase}</span>` : null;
                    })}
                  </div>
                  ${readOnly ? null : html`<button type="button" class="light-button" onClick=${() => {
                    const problems = store.copyViewFilters(sourceView);
                    setCopyProblem(problems.length > 0 ? 'copyViewTooDeep' : null);
                  }}><${Icon} name="IconCopy" size=${14} /><span>${t('audience.source.copyView')}</span></button>`}`
                : html`<p class="audience-builder-hint" role="note">${t('audience.problems.VIEW_NOT_AVAILABLE')}</p>`}
              ${copyProblem ? html`<p class="audience-builder-hint" role="alert">${t(`audience.source.${copyProblem}`)}</p>` : null}
              <p class="audience-builder-hint">${t('audience.source.viewPersonalHint')}</p>
            </div>`
          : null}
      </section>

      <section class="audience-builder-section" aria-labelledby=${`${idPrefix}-presets`}>
        <h4 id=${`${idPrefix}-presets`} class="audience-builder-section-title">${t('audience.presets.title')}</h4>
        <div class="audience-preset-row">
          <span class="audience-preset-label">${t('audience.presets.visit.label')}</span>
          <${SegmentedControl} label=${t('audience.presets.visit.label')} name="visit" value=${store.presets.value.visit} options=${visitOptions} disabled=${readOnly}
            onChange=${(visit) => store.setPresets({ visit: /** @type {any} */ (visit) })} />
        </div>
        <div class="audience-preset-row">
          <span class="audience-preset-label">${t('audience.presets.enrollment.label')}</span>
          <${SegmentedControl} label=${t('audience.presets.enrollment.label')} name="enrollment" value=${store.presets.value.enrollment} options=${enrollmentOptions} disabled=${readOnly}
            onChange=${(enrollment) => store.setPresets({ enrollment: /** @type {any} */ (enrollment) })} />
        </div>
        <div class="audience-preset-row">
          <span class="audience-preset-label">${t('audience.presets.lost.label')}</span>
          <${SegmentedControl} label=${t('audience.presets.lost.label')} name="lost" value=${store.presets.value.lost} options=${lostOptions} disabled=${readOnly}
            onChange=${(lost) => store.setPresets({ lost: /** @type {any} */ (lost) })} />
        </div>
        ${usage === 'AUDIENCE' ? html`<p class="audience-builder-hint">${t('audience.presets.notALeadNote')}</p>` : null}
      </section>

      <section class="audience-builder-section" aria-labelledby=${`${idPrefix}-conditions`}>
        <h4 id=${`${idPrefix}-conditions`} class="audience-builder-section-title">${t('audience.conditions.title')}</h4>
        ${root
          ? html`<div class="audience-conditions-header">
              <span>${t('audience.conditions.prefix')}</span>
              <select class="text-input audience-match-select" aria-label=${t('audience.conditions.title')} value=${root.logicalOperator} disabled=${readOnly}
                onChange=${(event) => store.setMatch(/** @type {'AND' | 'OR'} */ (event.currentTarget.value))}>
                <option value="AND">${t('audience.conditions.matchAll')}</option>
                <option value="OR">${t('audience.conditions.matchAny')}</option>
              </select>
            </div>
            <div class="audience-conditions-editor">
              <${FilterTreeEditor} objectMetadata=${objectMetadata} store=${store} fieldCatalog=${fieldCatalog}
                maximumRules=${AUDIENCE_LIMITS.filters} problemsByFilterId=${problemsByFilterId} readOnly=${readOnly} />
            </div>`
          : readOnly
            ? html`<p class="audience-builder-hint">${t('audience.conditions.none')}</p>`
            : html`<button type="button" class="light-button" onClick=${() => firstField && store.addFirstRule(firstField.id, getDefaultOperand(firstField))}>
                <${Icon} name="IconPlus" size=${14} /><span>${t('audience.conditions.add')}</span>
              </button>`}
        ${(previewState.preview?.ignoredFilterIds.length ?? 0) > 0
          ? html`<p class="audience-builder-hint" role="note">${t('audience.warnings.INCOMPLETE_RULES', { count: previewState.preview?.ignoredFilterIds.length ?? 0 })}</p>`
          : null}
      </section>

      ${showRecipients && usage === 'AUDIENCE'
        ? html`<section class="audience-builder-section" aria-labelledby=${`${idPrefix}-recipients`}>
            <h4 id=${`${idPrefix}-recipients`} class="audience-builder-section-title">${t('audience.recipients.label')}</h4>
            <div class="audience-recipient-options" role="radiogroup" aria-labelledby=${`${idPrefix}-recipients`}>
              ${['POINT_OF_CONTACT', 'ALL_GUARDIANS'].map((mode) => html`<label key=${mode} class="audience-recipient-option">
                <input type="radio" name=${`${idPrefix}-recipient-mode`} value=${mode} checked=${store.recipientMode.value === mode} disabled=${readOnly}
                  onChange=${() => store.setRecipientMode(/** @type {any} */ (mode))} />
                <span>${t(`audience.recipients.${mode}`)}</span>
              </label>`)}
            </div>
            <p class="audience-builder-hint">${t('audience.recipients.guardiansHint')}</p>
          </section>`
        : null}
    </div>

    <aside class="audience-builder-count">
      <${AudienceCountPanel}
        state=${previewState}
        stageField=${stageField}
        channels=${channels}
        showRecipients=${showRecipients && channels.length > 0}
        readOnly=${readOnly}
        onRetry=${runPreview}
        onRefresh=${runPreview}
        onSeeAll=${() => setIsCardsOpen(true)}
        isMobileOpen=${isMobileCountOpen}
        onToggleMobile=${() => setIsMobileCountOpen(!isMobileCountOpen)}
      />
    </aside>
    ${isCardsOpen ? html`<${AudienceCardsDialog} definition=${store.toDefinition()} usage=${usage} stageField=${stageField} onClose=${() => setIsCardsOpen(false)} />` : null}
  </div>`;
}
